<template>
  <div class="set-password-page">
    <!-- Background Pattern -->
    <div class="bg-pattern"></div>

    <!-- Main Container -->
    <div class="password-wrapper">
      <div class="password-container">
        <!-- Header Section -->
        <div class="header-section">
          <div class="logo-section">
            <img src="https://www.moh.gov.et/sites/default/files/inline-images/footer-logo_0.png" alt="MOH Logo" class="logo">
          </div>
          <h1 class="page-title text-white">Set Your Password</h1>
          <p class="page-subtitle">
            Create a secure password to protect your CBHI member account
          </p>
        </div>

        <!-- Alert Messages -->
        <transition name="slide-down">
          <div v-if="alert.success" class="alert alert-success">
            <i class="bx bx-check-circle"></i>
            <span>{{ message }}</span>
          </div>
        </transition>
        <transition name="slide-down">
          <div v-if="alert.error" class="alert alert-danger">
            <i class="bx bx-error-circle"></i>
            <span>{{ message }}</span>
          </div>
        </transition>

        <!-- Member Info Card -->
        <div class="member-info-card">
          <div class="member-icon">
            <i class="bx bx-user-circle"></i>
          </div>
          <div class="member-details">
            <span class="member-name">{{ memberName }}</span>
            <span class="member-cbhi">CBHI: {{ cbhiNumber }}</span>
          </div>
        </div>

        <!-- Password Form -->
        <form @submit.prevent="setPassword" class="password-form">
          <!-- New Password -->
          <div class="form-group">
            <label class="form-label">
              <i class="bx bx-lock-alt"></i>
              New Password
            </label>
            <div class="input-wrapper">
              <input :type="showPassword ? 'text' : 'password'"
                     v-model="form.password"
                     @input="checkPasswordStrength"
                     placeholder="Enter your password"
                     class="form-input"
                     :class="{ 'has-value': form.password }"
                     required>
              <button type="button" class="password-toggle" @click="togglePassword">
                <i :class="showPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
              </button>
            </div>
          </div>

          <!-- Password Strength Indicator -->
          <div v-if="form.password" class="strength-indicator">
            <div class="strength-bar-container">
              <div class="strength-bar"
                   :class="strengthClass"
                   :style="{ width: strengthPercentage + '%' }"></div>
            </div>
            <span class="strength-label" :class="strengthClass">
              {{ strengthText }}
            </span>
          </div>

          <!-- Password Requirements -->
          <div class="requirements-box">
            <div class="requirements-title">
              <i class="bx bx-info-circle"></i>
              Password Requirements
            </div>
            <div class="requirements-list">
              <div class="requirement-item" :class="{ met: requirements.length }">
                <i :class="requirements.length ? 'bx bx-check-circle' : 'bx bx-circle'"></i>
                <span>At least 8 characters long</span>
              </div>
              <div class="requirement-item" :class="{ met: requirements.uppercase }">
                <i :class="requirements.uppercase ? 'bx bx-check-circle' : 'bx bx-circle'"></i>
                <span>Contains uppercase letter (A-Z)</span>
              </div>
              <div class="requirement-item" :class="{ met: requirements.lowercase }">
                <i :class="requirements.lowercase ? 'bx bx-check-circle' : 'bx bx-circle'"></i>
                <span>Contains lowercase letter (a-z)</span>
              </div>
              <div class="requirement-item" :class="{ met: requirements.number }">
                <i :class="requirements.number ? 'bx bx-check-circle' : 'bx bx-circle'"></i>
                <span>Contains number (0-9)</span>
              </div>
              <div class="requirement-item optional" :class="{ met: requirements.special }">
                <i :class="requirements.special ? 'bx bx-check-circle' : 'bx bx-circle'"></i>
                <span>Contains special character (recommended)</span>
              </div>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="form-group">
            <label class="form-label">
              <i class="bx bx-lock-alt"></i>
              Confirm Password
            </label>
            <div class="input-wrapper">
              <input :type="showConfirmPassword ? 'text' : 'password'"
                     v-model="form.confirmPassword"
                     @input="checkPasswordMatch"
                     placeholder="Re-enter your password"
                     class="form-input"
                     :class="{
                       'has-value': form.confirmPassword,
                       'match-success': passwordsMatch && form.confirmPassword,
                       'match-error': !passwordsMatch && form.confirmPassword
                     }"
                     required>
              <button type="button" class="password-toggle" @click="toggleConfirmPassword">
                <i :class="showConfirmPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
              </button>
            </div>
            <div v-if="form.confirmPassword" class="password-match-indicator">
              <span v-if="passwordsMatch" class="match-success">
                <i class="bx bx-check"></i> Passwords match
              </span>
              <span v-else class="match-error">
                <i class="bx bx-x"></i> Passwords do not match
              </span>
            </div>
          </div>

          <!-- Security Tips -->
          <div class="security-tips">
            <div class="tips-title">
              <i class="bx bx-shield-check"></i>
              Password Security Tips
            </div>
            <ul class="tips-list">
              <li>Use a unique password you don't use elsewhere</li>
              <li>Don't share your password with anyone</li>
              <li>Avoid using personal information</li>
              <li>Change your password regularly</li>
            </ul>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button type="submit"
                    class="submit-btn"
                    :disabled="loading || !isFormValid">
              <span v-if="loading" class="btn-loading">
                <span class="spinner"></span>
                Setting Password...
              </span>
              <span v-else class="btn-content">
                <i class="bx bx-check-shield"></i>
                Set Password
              </span>
            </button>

            <button type="button"
                    class="skip-btn"
                    @click="skipForNow"
                    :disabled="loading">
              <i class="bx bx-skip-next"></i>
              Skip for Now (Continue with OTP only)
            </button>
          </div>
        </form>

        <!-- Help Section -->
        <div class="help-section">
          <div class="help-icon">
            <i class="bx bx-help-circle"></i>
          </div>
          <div class="help-content">
            <span class="help-title">Why set a password?</span>
            <span class="help-desc">
              A password allows faster login without waiting for OTP. You can still use OTP anytime.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: 'MemberSetPassword',

  data() {
    return {
      loading: false,
      showPassword: false,
      showConfirmPassword: false,

      form: {
        password: '',
        confirmPassword: ''
      },

      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      },

      passwordStrength: 0,
      passwordsMatch: false,

      alert: {
        success: false,
        error: false
      },
      message: '',

      memberName: '',
      cbhiNumber: ''
    };
  },

  computed: {
    strengthClass() {
      if (this.passwordStrength <= 25) return 'weak';
      if (this.passwordStrength <= 50) return 'fair';
      if (this.passwordStrength <= 75) return 'good';
      return 'strong';
    },

    strengthText() {
      if (this.passwordStrength <= 25) return 'Weak';
      if (this.passwordStrength <= 50) return 'Fair';
      if (this.passwordStrength <= 75) return 'Good';
      return 'Strong';
    },

    strengthPercentage() {
      return this.passwordStrength;
    },

    isFormValid() {
      return this.requirements.length &&
          this.requirements.uppercase &&
          this.requirements.lowercase &&
          this.requirements.number &&
          this.passwordsMatch;
    }
  },

  mounted() {
    this.loadMemberInfo();
    this.checkAuthentication();
  },

  methods: {
    loadMemberInfo() {
      const memberData = sessionStorage.getItem('memberData');
      const memberName = sessionStorage.getItem('memberName');
      const cbhiNumber = sessionStorage.getItem('cbhiNumber');

      if (memberData) {
        const member = JSON.parse(memberData);
        this.memberName = member.fullName || memberName || 'Member';
        this.cbhiNumber = member.cbhiNumber || cbhiNumber || '';
      } else {
        this.memberName = memberName || 'Member';
        this.cbhiNumber = cbhiNumber || '';
      }
    },

    checkAuthentication() {
      const token = localStorage.getItem('authToken');
      const loggedIn = sessionStorage.getItem('memberLoggedIn');

      if (!token || !loggedIn) {
        this.showAlert('error', 'Session expired. Please login again.');
        setTimeout(() => {
          router.push('/member-login');
        }, 2000);
      }
    },

    checkPasswordStrength() {
      const password = this.form.password;
      let strength = 0;

      // Check requirements
      this.requirements.length = password.length >= 8;
      this.requirements.uppercase = /[A-Z]/.test(password);
      this.requirements.lowercase = /[a-z]/.test(password);
      this.requirements.number = /[0-9]/.test(password);
      this.requirements.special = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      // Calculate strength
      if (this.requirements.length) strength += 20;
      if (this.requirements.uppercase) strength += 20;
      if (this.requirements.lowercase) strength += 20;
      if (this.requirements.number) strength += 20;
      if (this.requirements.special) strength += 20;

      this.passwordStrength = strength;

      // Check match if confirm password exists
      if (this.form.confirmPassword) {
        this.checkPasswordMatch();
      }
    },

    checkPasswordMatch() {
      this.passwordsMatch = this.form.password === this.form.confirmPassword;
    },

    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    async setPassword() {
      if (!this.isFormValid) {
        this.showAlert('error', 'Please meet all password requirements');
        return;
      }

      this.loading = true;
      this.clearAlerts();

      const data = {
        "function": "memberSetPassword",
        "newPassword": this.form.password,
        "confirmPassword": this.form.confirmPassword
      };

      try {
        const res = await executeJson(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          this.showAlert('success', res.data.message);

          // Wait 2 seconds then redirect to member portal
          setTimeout(() => {
            router.push('/member-portal');
          }, 2000);
        } else {
          this.showAlert('error', res.data.message);
        }
      } catch (error) {
        this.loading = false;
        this.showAlert('error', error.message || 'Failed to set password. Please try again.');
      }
    },

    skipForNow() {
      if (confirm('Are you sure you want to skip? You can set a password later in your account settings.')) {
        router.push('/member-portal');
      }
    },

    showAlert(type, msg) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = msg;

      if (type === 'success') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    clearAlerts() {
      this.alert = { success: false, error: false };
      this.message = '';
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.set-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.password-wrapper {
  width: 100%;
  max-width: 600px;
  position: relative;
  z-index: 1;
}

.password-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 30px;
  text-align: center;
  color: white;
}

.logo-section {
  margin-bottom: 20px;
}

.logo {
  height: 60px;
  filter: brightness(0) invert(1);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 10px;
}

.page-subtitle {
  font-size: 15px;
  opacity: 0.9;
  line-height: 1.5;
}

.alert {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  margin: 20px 30px 0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert i {
  font-size: 20px;
  margin-right: 10px;
}

.alert-success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.member-info-card {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 30px 30px 20px;
  border: 2px solid #e1e8ed;
}

.member-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.member-icon i {
  font-size: 32px;
  color: white;
}

.member-details {
  display: flex;
  flex-direction: column;
}

.member-name {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

.member-cbhi {
  font-size: 14px;
  color: #6c757d;
  font-family: 'Courier New', monospace;
}

.password-form {
  padding: 0 30px 30px;
}

.form-group {
  margin-bottom: 25px;
}

.form-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.form-label i {
  font-size: 18px;
  margin-right: 8px;
  color: #667eea;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 45px 14px 16px;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.form-input.match-success {
  border-color: #28a745;
}

.form-input.match-error {
  border-color: #dc3545;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.password-toggle:hover {
  background: #f8f9fa;
  color: #667eea;
}

.password-toggle i {
  font-size: 20px;
}

.strength-indicator {
  margin-top: 10px;
  margin-bottom: 20px;
}

.strength-bar-container {
  width: 100%;
  height: 6px;
  background: #e1e8ed;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.strength-bar {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 10px;
}

.strength-bar.weak {
  background: linear-gradient(90deg, #dc3545 0%, #ff6b6b 100%);
}

.strength-bar.fair {
  background: linear-gradient(90deg, #ffc107 0%, #ffdb4d 100%);
}

.strength-bar.good {
  background: linear-gradient(90deg, #17a2b8 0%, #4dd4e8 100%);
}

.strength-bar.strong {
  background: linear-gradient(90deg, #28a745 0%, #5cd67b 100%);
}

.strength-label {
  font-size: 13px;
  font-weight: 600;
}

.strength-label.weak {
  color: #dc3545;
}

.strength-label.fair {
  color: #ffc107;
}

.strength-label.good {
  color: #17a2b8;
}

.strength-label.strong {
  color: #28a745;
}

.requirements-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
  border: 2px solid #e1e8ed;
}

.requirements-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
}

.requirements-title i {
  font-size: 18px;
  margin-right: 8px;
  color: #667eea;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.requirement-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #6c757d;
  transition: all 0.3s ease;
}

.requirement-item i {
  font-size: 18px;
  margin-right: 10px;
  color: #cbd5e0;
  transition: all 0.3s ease;
}

.requirement-item.met {
  color: #28a745;
}

.requirement-item.met i {
  color: #28a745;
}

.requirement-item.optional {
  opacity: 0.7;
}

.password-match-indicator {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
}

.match-success {
  color: #28a745;
  display: flex;
  align-items: center;
}

.match-success i {
  margin-right: 5px;
  font-size: 16px;
}

.match-error {
  color: #dc3545;
  display: flex;
  align-items: center;
}

.match-error i {
  margin-right: 5px;
  font-size: 16px;
}

.security-tips {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
  border-left: 4px solid #ffc107;
}

.tips-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 12px;
}

.tips-title i {
  font-size: 18px;
  margin-right: 8px;
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tips-list li {
  font-size: 13px;
  color: #856404;
  padding: 6px 0;
  padding-left: 20px;
  position: relative;
}

.tips-list li:before {
  content: '•';
  position: absolute;
  left: 5px;
  font-weight: bold;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.submit-btn {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-content {
  display: flex;
  align-items: center;
}

.btn-content i {
  margin-right: 8px;
  font-size: 20px;
}

.btn-loading {
  display: flex;
  align-items: center;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.skip-btn {
  width: 100%;
  padding: 14px;
  background: white;
  color: #6c757d;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skip-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f8f9fa;
}

.skip-btn i {
  margin-right: 8px;
  font-size: 18px;
}

.help-section {
  display: flex;
  align-items: flex-start;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 25px 30px 30px;
  border-left: 4px solid #2196f3;
}

.help-icon {
  width: 40px;
  height: 40px;
  background: #2196f3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.help-icon i {
  font-size: 22px;
  color: white;
}

.help-content {
  display: flex;
  flex-direction: column;
}

.help-title {
  font-size: 14px;
  font-weight: 600;
  color: #1565c0;
  margin-bottom: 5px;
}

.help-desc {
  font-size: 13px;
  color: #1976d2;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .password-container {
    border-radius: 0;
  }

  .header-section {
    padding: 30px 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .password-form {
    padding: 0 20px 20px;
  }

  .member-info-card {
    margin: 20px 20px 15px;
  }

  .help-section {
    margin: 20px 20px 20px;
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>